import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import useSWR from "swr";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, TerminalSquare, Trash2, CornerDownLeft } from "lucide-react";
import { toast } from "sonner";

const fetcher = (u) => api.get(u).then((r) => r.data);

export function SiteConsole({ site }) {
  const { data: info } = useSWR(`/sites/${site.id}/containers`, fetcher);
  const [container, setContainer] = useState("");
  const [cmd, setCmd] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]); // [{cmd, exit_code, stdout, stderr, duration_ms, ts}]
  const [cmdHistory, setCmdHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (info && !container) setContainer(info.default);
  }, [info, container]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history]);

  const hasServer = info?.has_server;

  const run = async (e) => {
    e?.preventDefault?.();
    const c = cmd.trim();
    if (!c) return;
    if (!hasServer) {
      toast.error("Console requires a real server. Set a deploy target in the Env tab.");
      return;
    }
    setBusy(true);
    setCmdHistory((h) => (h[h.length - 1] === c ? h : [...h, c]));
    setHistIdx(-1);
    try {
      const { data } = await api.post(`/sites/${site.id}/exec`, {
        cmd: c, container: container || undefined,
      });
      setHistory((h) => [...h, { ...data, ts: new Date().toISOString() }]);
      setCmd("");
    } catch (err) {
      setHistory((h) => [...h, {
        cmd: c, exit_code: -1, stdout: "", stderr: err?.response?.data?.detail || "failed",
        duration_ms: 0, ts: new Date().toISOString(),
      }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (cmdHistory.length === 0) return;
      const ni = histIdx < 0 ? cmdHistory.length - 1 : Math.max(0, histIdx - 1);
      setHistIdx(ni);
      setCmd(cmdHistory[ni]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (histIdx < 0) return;
      const ni = histIdx + 1;
      if (ni >= cmdHistory.length) { setHistIdx(-1); setCmd(""); }
      else { setHistIdx(ni); setCmd(cmdHistory[ni]); }
    }
  };

  return (
    <Card data-testid="site-console-panel" className="p-0 border-border overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border surface-1 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <TerminalSquare className="h-4 w-4" /> Console
        </div>
        <div className="flex items-center gap-2">
          <select
            value={container}
            onChange={(e) => setContainer(e.target.value)}
            data-testid="console-container-select"
            className="h-8 text-xs rounded-md border border-input bg-transparent px-2"
          >
            {(info?.containers || []).map((c) => (
              <option key={c.name} value={c.name}>{c.label} — {c.name}</option>
            ))}
          </select>
          <Button variant="ghost" size="sm" className="gap-1.5"
                  onClick={() => setHistory([])}
                  data-testid="console-clear-button">
            <Trash2 className="h-3.5 w-3.5" /> Clear
          </Button>
        </div>
      </div>

      {!hasServer && (
        <div className="px-4 py-3 text-sm border-b border-border"
             style={{ background: "hsl(35 92% 12%)", color: "hsl(33 100% 92%)" }}>
          Console runs commands inside your site's container. This requires a real deploy target.
          Set one in the Env tab, then Deploy.
        </div>
      )}

      <div
        ref={scrollRef}
        className="scroll-thin overflow-auto p-4 font-mono text-[12.5px] leading-6 whitespace-pre code-surface rounded-none border-0"
        style={{ height: 420, minHeight: 320 }}
      >
        {history.length === 0 && (
          <div className="text-muted-foreground">
            {hasServer
              ? `Ready. Container: ${container}\nType a shell command below and press Enter. Try: ls, env, whoami, cat /etc/os-release`
              : "Console idle — deploy this site to a server to start using it."}
          </div>
        )}
        {history.map((h, i) => (
          <div key={i}>
            <div className="text-primary">$ {h.cmd}</div>
            {h.stdout && <div>{h.stdout}</div>}
            {h.stderr && <div className="text-red-400">{h.stderr}</div>}
            <div className="text-[10.5px] text-muted-foreground">
              exit={h.exit_code} · {h.duration_ms}ms
            </div>
            <div className="mt-2" />
          </div>
        ))}
      </div>

      <form onSubmit={run} className="flex items-center gap-2 border-t border-border p-3 surface-1">
        <span className="font-mono text-primary shrink-0">$</span>
        <Input
          value={cmd}
          onChange={(e) => setCmd(e.target.value)}
          onKeyDown={onKey}
          placeholder={hasServer ? "ls -la" : "deploy to a server to enable"}
          className="font-mono h-9"
          data-testid="console-command-input"
          disabled={!hasServer || busy}
          autoFocus
        />
        <Button type="submit" size="sm" className="gap-1.5"
                data-testid="console-run-button"
                disabled={!hasServer || busy || !cmd.trim()}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CornerDownLeft className="h-3.5 w-3.5" />}
          Run
        </Button>
      </form>
    </Card>
  );
}
